import {createAsyncThunk} from '@reduxjs/toolkit'

import {FailedResultCode, PaymentGatewayCode, PayMethod, FAILED_RESULT_CODE} from '@web/core/constants'
import {
    canHaveCashReceiptSelector,
    paymentGatewayFieldsSelector,
    secondaryPaymentGatewayFieldsSelector,
} from '@web/selectors/priorityPaymentMethodInfo'
import {AgreementState} from '@web/slices/agreement/slice'
import {CashReceiptState, cashReceiptSelector} from '@web/slices/cashReceipt/slice'
import {ChargePointState} from '@web/slices/chargePoint/slice'
import {CommonState} from '@web/slices/common/slice'
import {MerchantsState} from '@web/slices/merchants/slice'
import {naverPayPointSelector, PointPaymentState} from '@web/slices/pointPayment/slice'
import {PriorityPaymentMethodState} from '@web/slices/priorityPaymentMethod/slice'
import {PurchaserState, purchaserSelector} from '@web/slices/purchaser/slice'
import {RewardPointState} from '@web/slices/rewardPoint/slice'

import api from '../apis'
import {PaymentMethodInfoSuccessResult} from '../apis/interfaces'

import {
    RegisterPaymentMethodsRequest,
    RegistPaymentMethodsParams,
    InitPaymentMethodManagementParams,
    InitPaymentMethodManagementPayload,
} from './interfaces'

export type {ErrorResponse} from './interfaces'

type RootState = {
    agreement: AgreementState
    cashReceipt: CashReceiptState
    chargePoint: ChargePointState
    common: CommonState
    merchants: MerchantsState
    pointPayment: PointPaymentState
    priorityPaymentMethod: PriorityPaymentMethodState
    purchaser: PurchaserState
    rewardPoint: RewardPointState
}

type ThunkApi<RejectValue> = {
    state: RootState
    rejectValue: RejectValue
}

export const initPaymentMethodManagement = createAsyncThunk<
    InitPaymentMethodManagementPayload,
    InitPaymentMethodManagementParams,
    ThunkApi<Constant<typeof FAILED_RESULT_CODE>>
>('init/PaymentMethodManagement', async ({entry}) => {
    // type fix 필요
    const response = (await api.fetchPaymentMethodInfo({entry})) as unknown as PaymentMethodInfoSuccessResult

    return {...response, entry}
})

export const registerPaymentMethods = createAsyncThunk<void, RegisterPaymentMethodsRequest, ThunkApi<FailedResultCode>>(
    'register/booking',
    async ({entry}, {getState}) => {
        const state = getState()
        const {usePointAll} = naverPayPointSelector(state)
        const canHaveCashReceipt = canHaveCashReceiptSelector(state)
        const primaryPaymentGatewayFields = paymentGatewayFieldsSelector(state)
        const secondaryPaymentGatewayFields = secondaryPaymentGatewayFieldsSelector(state)
        const {
            issueType: {current},
            issueNumber,
            prevUsageType: usageType,
        } = cashReceiptSelector(state)
        const {isPaymentMethodRegistered} = purchaserSelector(state)

        if (!primaryPaymentGatewayFields) {
            return
        }

        const cashReceipt: RegistPaymentMethodsParams['cashReceipt'] =
            canHaveCashReceipt && !!current && usageType
                ? {
                      usageType,
                      issueType: current,
                      businessRegistrationNumberFirst: issueNumber.BUSINESS_REGISTRATION_NUMBER[0] ?? null,
                      businessRegistrationNumberSecond: issueNumber.BUSINESS_REGISTRATION_NUMBER[1] ?? null,
                      businessRegistrationNumberThird: issueNumber.BUSINESS_REGISTRATION_NUMBER[2] ?? null,
                      mobilePhoneNumberFirst: issueNumber.PR_CELL_PHONE_NUMBER[0] ?? null,
                      mobilePhoneNumberSecond: issueNumber.PR_CELL_PHONE_NUMBER[1] ?? null,
                      mobilePhoneNumberThird: issueNumber.PR_CELL_PHONE_NUMBER[2] ?? null,
                      cashReceiptCardNumberFirst: issueNumber.PR_CASH_RECEIPT_CARD_NUMBER[0] ?? null,
                      cashReceiptCardNumberSecond: issueNumber.PR_CASH_RECEIPT_CARD_NUMBER[1] ?? null,
                      cashReceiptCardNumberThird: issueNumber.PR_CASH_RECEIPT_CARD_NUMBER[2] ?? null,
                      cashReceiptCardNumberFourth: issueNumber.PR_CASH_RECEIPT_CARD_NUMBER[3] ?? null,
                  }
                : null

        const getPayMethodType = (pgcd: string) => {
            if (pgcd === PaymentGatewayCode.PAY_POINT) {
                return PayMethod.RECHARGE_POINT
            }

            return PayMethod.CARD_EASY_PAY
        }

        // 현금영수증을 신청할 수 있고, 현금영수증 발급 타입이 있다면 현금영수증 신청
        const cashReceiptApply = canHaveCashReceipt && !!current

        const requestParams: RegistPaymentMethodsParams = {
            cashReceipt,
            usePointAll,
            firstPayMethod: {
                corporationCode: primaryPaymentGatewayFields.corpcd,
                key: primaryPaymentGatewayFields.corpkey,
                payMethodType: getPayMethodType(primaryPaymentGatewayFields.pgcd),
            },
            secondPayMethod: secondaryPaymentGatewayFields
                ? {
                      corporationCode: secondaryPaymentGatewayFields.corpcd,
                      key: secondaryPaymentGatewayFields.corpkey,
                      payMethodType: getPayMethodType(secondaryPaymentGatewayFields.pgcd),
                  }
                : undefined,
            entry,
            cashReceiptApply,
        }
        if (isPaymentMethodRegistered) {
            await api.updatePaymentMethod(requestParams)
        } else {
            await api.registerPaymentMethod(requestParams)
        }
    },
)
